<template>
  <!-- Main grid container for the draggable layout system -->
  <div
    ref="gridContainer"
    class="grid-layout__main-container grid-stack"
  >
    <!-- Dynamic MainSlot components for each grid item -->
    <main-slot
      v-for="(item, index) in gridItems"
      :key="item.id"
      :id="item.id"
      :index="index"
      :itemData="item"
      :tabId="tabId"
      :slots="gridItems"
      :pos="item.pos"
      :isAltPressed="state.isAltPressed"
      :isCard="isCard"
      @remove-slot="removeSlot"
    />
  </div>
</template>
<script>
import {
  reactive,
  onMounted,
  ref,
  nextTick,
  computed,
  watch,
  onBeforeUnmount,
} from "@vue/composition-api";
import "gridstack/dist/gridstack.min.css";
import { GridStack } from "gridstack";
import store from "@/store";
import defaultLayout from "@/config/defaultLayout";
import statisticsLayout from "@/config/statisticsLayout";
import liveVideoLayout from "@/config/liveVideoLayout";
import liveVideo5x1Layout from "@/config/liveVideo5x1Layout";
import timelineLayout from "@/config/timelineLayout";
import starterLayout from "@/config/starterLayout";
import maintenanceLayout from "@/config/maintenanceLayout";
import MainSlot from "./MainSlot.vue";
import { v4 as uuidv4 } from "uuid";
import { EventBus } from "@/libs/event-bus";
import { debounce } from "lodash";

export default {
  name: "MainLayout",
  components: { MainSlot },
  props: {
    // Tab identifier for the current layout instance
    tabId: {
      type: [String, Number],
      required: true,
    },
    // Flag to determine if this tab is currently active
    isActive: {
      type: Boolean,
      default: false,
    },
    tabHeight: {
      type: Number,
      default: 0,
    },
  },
  setup(props) {
    // Maximum height (in rows) that any single slot/widget can occupy
    const MAX_SLOT_HEIGHT = 8;

    // Reactive state object containing grid configuration and status
    const state = reactive({
      grid: null, // GridStack instance reference
      maxRows: 8, // Maximum number of rows in the grid
      //maxRows is 8 by default, but it will be set to 0 (no limit)
      cellHeight: 80, // Height of each grid cell in pixels
      minRow: 8, // Minimum number of rows
      margin: 0, // Spacing between grid items
      isAltPressed: false, // Flag to track Alt key state for drag/resize functionality
      isResizing: false, // Flag to track if a widget is currently being resized
      resizingNodeId: null, // ID of the node currently being resized
    });

    // Template reference for the main grid container
    const gridContainer = ref(null);

    // Local handler reference for EventBus to properly unsubscribe
    let onAddSlotHandler = null;
    // Debounced handler for grid change events to avoid redundant store updates
    let debouncedSyncSlots = null;
    // Saved reference to GridStack's collision routine while it's disabled
    // during a resize (see freezeNonResizingNodes / unfreezeAllNodes)
    let originalFixCollisions = null;

    // Variable to store layout history for undo functionality, with a maximum of 20 snapshots
    const MAX_HISTORY = 20;
    const layoutHistory = ref([]);
    // Flag to defer grid reload until the tab becomes active
    const needsReload = ref(false);

    /**
     * Prevents any ancestor element from scrolling.
     * Ensures the top 8 rows are always visible without scroll displacement.
     */
    const preventAncestorScroll = () => {
      if (!gridContainer.value) return;
      gridContainer.value.scrollTop = 0;
      const tabPane = gridContainer.value.closest('.tab-pane');
      if (tabPane) tabPane.scrollTop = 0;
      const tabContent = gridContainer.value.closest('.tab-content');
      if (tabContent) tabContent.scrollTop = 0;
    };

    // ── Resize-to-delete helpers ──────────────────────────────────────

    /**
     * Calculates what percentage of nodeB's area is covered by nodeA
     * using the grid-unit coordinates (x, y, w, h) from GridStack nodes.
     * @param {Object} nodeA - The resizing node {x, y, w, h}
     * @param {Object} nodeB - The target node to check coverage {x, y, w, h}
     * @returns {number} Overlap ratio (0-1)
     */
    const calculateOverlapPercentage = (nodeA, nodeB) => {
      const overlapX = Math.max(0,
        Math.min(nodeA.x + nodeA.w, nodeB.x + nodeB.w) - Math.max(nodeA.x, nodeB.x)
      );
      const overlapY = Math.max(0,
        Math.min(nodeA.y + nodeA.h, nodeB.y + nodeB.h) - Math.max(nodeA.y, nodeB.y)
      );
      const overlapArea = overlapX * overlapY;
      const nodeBArea = nodeB.w * nodeB.h;
      return nodeBArea > 0 ? overlapArea / nodeBArea : 0;
    };

    /**
     * Freezes the rest of the layout while a widget is being resized.
     *
     * GridStack would normally push neighbouring widgets out of the way as the
     * resized widget grows over them (engine._fixCollisions). We swap that
     * routine for a no-op until the resize ends so every other widget stays
     * exactly where it is — the widgets the resize lands on are handled later
     * in removeOverlappedSlots() on resizestop.
     */
    const freezeNonResizingNodes = () => {
      if (!state.grid || originalFixCollisions) return;
      originalFixCollisions = state.grid.engine._fixCollisions;
      state.grid.engine._fixCollisions = () => false;
    };

    /**
     * Restores GridStack's normal collision handling after a resize ends.
     */
    const unfreezeAllNodes = () => {
      if (!state.grid || !originalFixCollisions) return;
      state.grid.engine._fixCollisions = originalFixCollisions;
      originalFixCollisions = null;
    };

    /**
     * Decides what happens to the widgets the resized node ended up on top of:
     *   - covered ≥ 30 % → the widget is removed (replaced by the resized one)
     *   - covered  < 30 % → the widget is pushed just below the resized node so
     *     the layout never ends up with two overlapping widgets.
     * @param {string} resizingNodeId - ID of the node that was resized
     */
    const removeOverlappedSlots = (resizingNodeId) => {
      const nodes = [...state.grid.engine.nodes];
      const resizingNode = nodes.find((n) => n.id === resizingNodeId);
      if (!resizingNode) return;

      const OVERLAP_THRESHOLD = 0.01;
      const slotsToRemove = [];
      const slotsToPush = [];

      nodes.forEach((node) => {
        if (node.id === resizingNodeId) return;
        const overlap = calculateOverlapPercentage(resizingNode, node);
        if (overlap >= OVERLAP_THRESHOLD) {
          slotsToRemove.push(node.id);
        } else if (overlap > 0) {
          slotsToPush.push(node);
        }
      });

      // Remove every heavily-overlapped slot. The resizestart handler already
      // captured a snapshot, so skip per-slot snapshots — one Ctrl+Z then
      // restores the whole resize, including every slot it removed.
      slotsToRemove.forEach((slotId) => {
        removeSlot(slotId, { skipSnapshot: true });
      });

      // Push the lightly-overlapped slots out from under the resized node
      slotsToPush.forEach((node) => {
        if (node.el && state.grid.engine.nodes.includes(node)) {
          state.grid.update(node.el, { y: resizingNode.y + resizingNode.h });
        }
      });
    };

    /**
     * Saves a deep-cloned snapshot of the current gridItems and slots
     * into the history stack. Called before any layout-modifying operation.
     */
    const saveSnapshot = () => {
      const currentItems = gridItems.value || [];
      const currentSlots = currentTab.value.slots || [];
      const snapshot = {
        gridItems: JSON.parse(JSON.stringify(currentItems)),
        slots: JSON.parse(JSON.stringify(currentSlots)),
      };
      layoutHistory.value.push(snapshot);
      // Keep history bounded
      if (layoutHistory.value.length > MAX_HISTORY) {
        layoutHistory.value.shift();
      }
    };

    /**
     * Restores the last snapshot from the history stack (Ctrl+Z).
     * Re-applies gridItems and slots to the store and reloads the grid.
     */
    const undoLastChange = () => {
      if (layoutHistory.value.length === 0) return;

      const snapshot = layoutHistory.value.pop();

      // Restore gridItems
      store.commit("grid/updateTabLayoutGrid", {
        tabId: props.tabId,
        gridItems: snapshot.gridItems,
      });

      // Restore slots
      if (snapshot.slots.length > 0) {
        store.commit("grid/updateTabSlots", {
          tabId: props.tabId,
          slots: snapshot.slots,
        });
      }

      // Reload grid visually
      nextTick(() => {
        loadGrid();
        updateCellHeight();
        nextTick(() => checkCanAddSlot());
      });
    };

    // Computed properties for reactive data access
    const currentTab = computed(() => store.state.grid.tabs[props.tabId] || {});
    const fullScreenEnabled = computed(() => store.state.grid.fullScreenEnabled);
    const isCard = computed(() => layout.value.isCard === 1);

    // Calculate spacing distance for card layout mode
    const cardDistance = computed(() => {
      // If card mode is disabled, distance should be 0
      if (layout.value.isCard === 0) return 0;
      return layout.value.distance || 0;
    });

    const gridItems = computed(() => currentTab.value.gridItems || []);

    // Dynamic layout configuration based on current tab's layout type
    const layout = computed(() => {
      if (!currentTab.value.layout) return defaultLayout;
      let returnStatement = {};

      // Switch between different layout types
      switch (currentTab.value.layout) {
        case "default":
          // Default layout for new users or when explicitly requested
          returnStatement = defaultLayout;
          break;
        case "standalone":
          // Standalone layout uses current tab's slots configuration
          returnStatement = {
            id: "standalone",
            name: "standalone",
            grid: currentTab.value.slots,
          };
          break;
        case "statistics":
          returnStatement = statisticsLayout;
          break;
        case "timeline":
          returnStatement = timelineLayout;
          break;
        case "starter":
          returnStatement = starterLayout;
          break;
        case "maintenance-view":
          returnStatement = maintenanceLayout;
          break;
        case "unit-live":
        case "unit-recording":
          // Choose between 5x1 or standard video layout based on slot count
          returnStatement =
            currentTab.value.slots.length > 4 ? liveVideo5x1Layout : liveVideoLayout;
          break;
        default:
          // Fetch user-defined layouts from store
          const userLayouts = store.getters["layouts/getUserLayouts"];
          const layout = userLayouts.find((l) => l.id === currentTab.value.layout);
          returnStatement = layout || defaultLayout;
          break;
      }

      return returnStatement;
    });

    /**
     * Dynamically calculates and updates the cell height based on container size
     * This ensures the grid fills the available space properly
     */
    const updateCellHeight = () => {
      // Skip expensive DOM measurements for inactive tabs
      if (!props.isActive) return;

      const containerHeight = props.tabHeight;

      if (containerHeight > 0) {
        // Calculate height per row minus padding/margin
        const calculatedHeight = containerHeight / 8 - 4;
        state.cellHeight = Math.max(calculatedHeight, 10); // Minimum 10px height
      } else {
        state.cellHeight = 80; // Fallback height
      }

      // Update GridStack instance with new cell height
      if (state.grid) {
        state.grid.cellHeight(state.cellHeight);
      }
    };

    /**
     * Initializes or reloads the grid with current items
     * Clears existing widgets and recreates them from current state
     */
    const loadGrid = () => {
      // Remove all existing widgets if grid has content
      if (state.grid && state.grid.engine.nodes.length > 0) {
        state.grid.removeAll(false);
      }

      // Wait for DOM update before adding widgets
      nextTick(() => {
        // The grid may not exist yet (called before mount) or may be gone
        if (!state.grid || !gridContainer.value) return;
        const itemElements = gridContainer.value.querySelectorAll(".grid-stack-item");

        // Bulk-load like GridStack's load(): batch + _loading + no maxRow so
        // overlapping layouts can't recurse _fixCollisions into a stack overflow.
        state.grid.batchUpdate();
        state.grid.engine._loading = true;
        const prevMaxRow = state.grid.engine.maxRow;
        state.grid.engine.maxRow = 0;
        try {
          itemElements.forEach((el, index) => {
            const item = gridItems.value[index];
            if (item) {
              state.grid.makeWidget(el); // Register element as GridStack widget
              // Enforce maximum height constraint per widget
              const clampedH = Math.min(item.h, MAX_SLOT_HEIGHT);
              state.grid.update(el, { maxH: MAX_SLOT_HEIGHT, h: clampedH });
            }
          });
        } finally {
          state.grid.engine.maxRow = prevMaxRow;
          delete state.grid.engine._loading;
          state.grid.batchUpdate(false);
        }

        updateCellHeight();
      });
    };

    /**
     * Enables drag and resize functionality for grid items
     */
    const enableDragResize = () => {
      if (!state.grid) return;
      state.grid.enable();
    };

    /**
     * Disables drag and resize functionality for grid items
     */
    const disableDragResize = () => {
      if (!state.grid) return;
      // Safety net: if a resize was interrupted (e.g. Alt released mid-drag)
      // make sure GridStack's collision handling is back to normal.
      unfreezeAllNodes();
      state.grid.disable();
    };

    /**
     * Handles Alt key press to enable drag/resize mode
     */
    const handleKeyDown = (event) => {
      // Ctrl+Z: undo last layout change
      if ((event.ctrlKey || event.metaKey) && event.key === "z") {
        event.preventDefault();
        undoLastChange();
        return;
      }

      if (!state.isAltPressed && event.key === "Alt") {
        state.isAltPressed = true;
        store.dispatch("grid/setAltPressed", true);
        enableDragResize();
      }
    };

    /**
     * Handles Alt key release to disable drag/resize mode
     */
    const handleKeyUp = async (event) => {
      if (state.isAltPressed) {
        state.isAltPressed = false;
        store.dispatch("grid/setAltPressed", false);
        disableDragResize();
      }
    };

    /**
     * Handles window blur to disable drag/resize mode
     */
    const handleWindowBlur = () => {
      if (state.isAltPressed) {
        state.isAltPressed = false;
        store.dispatch("grid/setAltPressed", false);
        disableDragResize();
      }
    };

    /**
     * Sorts grid items by their position (top-to-bottom, left-to-right)
     * @param {Array} items - Array of grid items to sort
     * @returns {Array} Sorted array of items
     */
    const sortItemsByPosition = (items) => {
      return [...items].sort((a, b) => {
        if (a.y !== b.y) return a.y - b.y; // Sort by row first
        return a.x - b.x; // Then by column
      });
    };

    /**
     * Updates grid items and corresponding slots in the store after drag/resize operations
     * Synchronizes the visual grid state with the application state
     */
    const updateGridItemsAndSlots = (updateItemCallback = null) => {
      // Get current grid node positions from GridStack engine
      const nodes = [...state.grid.engine.nodes];
      const currentItems = gridItems.value || [];

      // Update items with new positions from grid nodes
      let updatedItems = currentItems.map((item) => {
        const node = nodes.find((n) => n.id === item.id);
        if (node) {
          const updatedItem = {
            ...item,
            x: node.x, // Grid column position
            y: node.y, // Grid row position
            w: node.w, // Width in grid units
            h: Math.min(node.h, MAX_SLOT_HEIGHT), // Height clamped to max
          };
          return updateItemCallback ? updateItemCallback(updatedItem, node) : updatedItem;
        }
        return item;
      });

      // Sort items by position and update their sequential position numbers
      const sortedItems = sortItemsByPosition(updatedItems);
      const reorderedItems = sortedItems.map((item, index) => ({
        ...item,
        pos: index + 1, // Sequential position number
      }));

      // Update grid items in store
      store.commit("grid/updateTabLayoutGrid", {
        tabId: props.tabId,
        gridItems: reorderedItems,
      });

      // Update corresponding slots if they exist
      const currentSlots = currentTab.value.slots || [];
      if (currentSlots.length > 0) {
        const updatedSlots = currentSlots.map((slot) => {
          const matchingItem = reorderedItems.find((item) => item.id === slot.id);
          if (matchingItem) {
            return {
              ...slot,
              pos: matchingItem.pos,
              x: matchingItem.x,
              y: matchingItem.y,
              w: matchingItem.w,
              h: matchingItem.h,
            };
          }
          return slot;
        });

        // Update slots in store
        store.commit("grid/updateTabSlots", {
          tabId: props.tabId,
          slots: updatedSlots,
        });
      }
    };

    /**
     * Checks if there is available space in the grid to add a new widget.
     * Only considers space available when maxRow is at its default limit (8).
     * Updates the store so the navbar can show/hide the add slot button.
     */
    const checkCanAddSlot = () => {
      // Only the active tab should update the global canAddSlot state
      if (!props.isActive) return;
      if (!state.grid || state.isResizing) {
        store.dispatch("grid/setCanAddSlot", false);
        return;
      }

      // If any widget overflows beyond the 8x12 grid, disallow adding slots
      const nodes = state.grid.engine.nodes || [];
      const totalCols = state.grid.getColumn();
      const isOverflowing = nodes.some((n) => n.y + n.h > 8 || n.x + n.w > totalCols);
      if (isOverflowing) {
        store.dispatch("grid/setCanAddSlot", false);
        return;
      }

      const widgetWidth = 2;
      const widgetHeight = 1;
      let hasSpace = false;

      for (let y = 0; y < 8; y++) {
        for (let x = 0; x <= totalCols - widgetWidth; x++) {
          if (state.grid.isAreaEmpty(x, y, widgetWidth, widgetHeight)) {
            hasSpace = true;
            break;
          }
        }
        if (hasSpace) break;
      }

      store.dispatch("grid/setCanAddSlot", hasSpace);
    };

    /**
     * Attempts to add a new widget to the grid in the first available space
     * Searches for empty space from top-left to bottom-right
     */
    const tryAddWidget = () => {
      const widgetWidth = 2;
      const widgetHeight = 1;

      // Get current number of columns in the grid
      const totalCols = state.grid.getColumn();
      let widgetAdded = false;
      let newWidget = null;

      // Find the highest position number for the new widget
      let maxPos = 0;
      const currentItems = gridItems.value || [];
      currentItems.forEach((item) => {
        if (item.pos > maxPos) {
          maxPos = item.pos;
        }
      });

      // Search for available space in the grid
      for (let y = 0; y < 8; y++) {
        for (let x = 0; x <= totalCols - widgetWidth; x++) {
          // Check if area is empty and widget will fit
          if (state.grid.isAreaEmpty(x, y, widgetWidth, widgetHeight)) {
            if (state.grid.willItFit(x, y, widgetWidth, widgetHeight, true)) {
              // Create new widget object
              newWidget = {
                id: uuidv4(), // Generate unique ID
                x: x,
                y: y,
                w: widgetWidth,
                h: widgetHeight,
                minW: widgetWidth, // Minimum width constraint
                minH: widgetHeight, // Minimum height constraint
                maxH: 8, // Maximum height constraint (8 rows)
                pos: maxPos + 1, // Sequential position
                content: `${widgetWidth} x ${widgetHeight}`,
                name: null,
                type: null,
                data: null,
              };

              // Update store with new widget
              const updatedGridItems = [...currentItems, newWidget];
              store.commit("grid/updateTabLayoutGrid", {
                tabId: props.tabId,
                gridItems: updatedGridItems,
              });

              // Synchronize slots with grid items
              store.dispatch("grid/syncSlotsWithGridItems", {
                tabId: props.tabId,
              });

              widgetAdded = true;
              break;
            }
          }
        }
        if (widgetAdded) break;
      }

      // Register new widget with GridStack after DOM update
      if (widgetAdded && newWidget) {
        nextTick(() => {
          const widgetEl = gridContainer.value.querySelector(
            `[id="${newWidget.id}"] , [gs-id="${newWidget.id}"]`
          );
          if (widgetEl) {
            state.grid.makeWidget(widgetEl);
          }
          nextTick(() => checkCanAddSlot());
        });
      } else {
        console.log("There is no space available to add the widget");
      }
    };

    /**
     * Removes a slot/widget from the grid and store
     * @param {String} itemId - ID of the item to remove
     * @param {Object} [options]
     * @param {Boolean} [options.skipSnapshot=false] - skip pushing a history
     *   snapshot when the caller already captured one (e.g. a single resize
     *   that removes several slots at once, so one Ctrl+Z restores them all)
     */
    const removeSlot = (itemId, { skipSnapshot = false } = {}) => {
      // Save snapshot before removing so it can be undone
      if (!skipSnapshot) saveSnapshot();

      const currentItems = gridItems.value || [];
      const index = currentItems.findIndex((item) => item.id === itemId);

      if (index !== -1) {
        // Remove widget from GridStack
        const widgetEl = gridContainer.value.querySelector(`[id="${itemId}"]`);
        if (widgetEl) {
          state.grid.removeWidget(widgetEl, false);
        }

        // Remove from store
        store.dispatch("grid/removeItemById", {
          tabId: props.tabId,
          itemId: itemId,
        });

        store.dispatch("grid/updateUserConfig");
        nextTick(() => checkCanAddSlot());
      }
    };

    // Watchers for reactive updates

    // Watch for card distance changes and update grid margin
    watch(cardDistance, (newDistance) => {
      if (state.grid) {
        state.grid.margin(newDistance);
      }
    });

    // When tab becomes active, apply any deferred updates
    watch(
      () => props.isActive,
      (newValue) => {
        if (newValue) {
          if (needsReload.value) {
            // Grid layout changed while tab was inactive — reload now
            needsReload.value = false;
            nextTick(() => {
              loadGrid();
              nextTick(() => checkCanAddSlot());
            });
          } else {
            setTimeout(() => {
              updateCellHeight();
              checkCanAddSlot();
            }, 50);
          }
        }
      }
    );

    watch(
      () => props.tabHeight,
      () => {
        setTimeout(() => {
          updateCellHeight();
        }, 10);
      }
    );

    // Update cell height when fullscreen mode changes
    watch(fullScreenEnabled, (newValue) => {
      setTimeout(() => {
        updateCellHeight();
      }, 50);
    });

    // Reload grid when layout configuration changes
    watch(
      layout,
      (newLayout, oldLayout) => {
        if (state.grid) {
          state.grid.disable();
          if (props.isActive) {
            nextTick(() => {
              loadGrid();
              updateCellHeight();
              if (!state.isAltPressed) {
                state.grid.disable();
              }
            });
          } else {
            // Defer reload until this tab becomes active
            needsReload.value = true;
          }
        }
      },
      { deep: true }
    );

    watch(
      () =>
        (gridItems.value || [])
          .map((i) => `${i.id}:${i.x}:${i.y}:${i.w}:${i.h}:${i.pos}`)
          .join("|"),
      () => {
        // Items can change before onMounted creates the GridStack instance
        // (e.g. user config arriving async). onMounted always runs loadGrid()
        // with the latest items, so there is nothing to do yet — calling
        // loadGrid() here would crash on state.grid.batchUpdate().
        if (!state.grid) return;
        if (!props.isActive) {
          // Grid items changed while inactive — defer reload
          needsReload.value = true;
          return;
        }
        // Do not reload the grid if Alt is pressed (delete mode active)
        if (!state.isAltPressed) {
          loadGrid();
        }
      }
    );

    // Lifecycle hooks
    onMounted(() => {
      // Initialize cell height calculation
      updateCellHeight();

      // Initialize GridStack with configuration
      state.grid = GridStack.init(
        {
          cellHeight: state.cellHeight,
          acceptWidgets: true, // Allow widgets to be dropped
          float: true, // Allow floating widgets
          margin: cardDistance.value, // Spacing between items
          maxRow: state.maxRows,
          minRow: state.minRow,
          resizable: { handles: "n,ne,e,se,s,sw,w" }, // Enable resize handles
          draggable: { handle: ".grid-stack-item" }, // Drag handle selector
          animate: false, // Disable animations
          auto: false,
        },
        gridContainer.value
      );

      // Load existing grid items
      nextTick(() => {
        loadGrid();
        nextTick(() => checkCanAddSlot());
      });

      // Start with drag/resize disabled
      state.grid.disable();

      // Event listeners for grid interactions

      // Handle drag start - save snapshot before drag changes positions
      state.grid.on("dragstart", (_, el) => {
        saveSnapshot();
      });

      // Handle drag stop event - update positions in store
      state.grid.on("dragstop", (_, el) => {
        updateGridItemsAndSlots();
        preventAncestorScroll();
        nextTick(() => checkCanAddSlot());
      });

      // Handle resize start - save snapshot and unlock maxRow
      state.grid.on("resizestart", (_, el) => {
        saveSnapshot();
        state.isResizing = true;
        state.resizingNodeId = el.gridstackNode?.id || null;
        store.dispatch("grid/setCanAddSlot", false);
        state.grid.engine.maxRow = 0; // 0 means no limit
        state.grid.opts.maxRow = 0;
        // Freeze the rest of the layout so it doesn't reflow while resizing
        freezeNonResizingNodes();
        preventAncestorScroll();
      });

      // Handle resize stop event - restore maxRow limit and update positions
      state.grid.on("resizestop", (_, el) => {
        state.isResizing = false;
        state.grid.engine.maxRow = state.maxRows;
        state.grid.opts.maxRow = state.maxRows;

        // Restore GridStack's normal collision handling before touching nodes
        unfreezeAllNodes();

        // Clamp any widget that exceeds the maximum slot height
        const nodes = state.grid.engine.nodes || [];
        nodes.forEach((node) => {
          if (node.h > MAX_SLOT_HEIGHT) {
            state.grid.update(node.el, { h: MAX_SLOT_HEIGHT });
          }
        });

        // Handle the widgets the resized node landed on (remove or push aside)
        if (state.resizingNodeId) {
          removeOverlappedSlots(state.resizingNodeId);
          state.resizingNodeId = null;
        }

        updateGridItemsAndSlots();
        preventAncestorScroll();
        nextTick(() => {
          updateCellHeight();
          checkCanAddSlot();
          preventAncestorScroll();
        });
      });

      // Handle general changes - sync slots with grid items (debounced to
      // avoid redundant store commits and API calls during drag/resize)
      debouncedSyncSlots = debounce(() => {
        if (props.isActive) {
          store.dispatch("grid/syncSlotsWithGridItems", {
            tabId: props.tabId,
          });
        }
      }, 50);
      state.grid.on("change", () => {
        debouncedSyncSlots();
        preventAncestorScroll();
      });

      // Global event listeners
      window.addEventListener("resize", updateCellHeight);
      window.addEventListener("keydown", handleKeyDown);
      window.addEventListener("keyup", handleKeyUp);
      window.addEventListener("blur", handleWindowBlur);

      // Listen for global request to add a slot in the first available space
      onAddSlotHandler = (payload) => {
        // Only handle event for this tab instance
        const targetTabId =
          payload && payload.tabId ? payload.tabId : store.state.grid.activeTab;
        if (String(targetTabId) === String(props.tabId)) {
          tryAddWidget();
        }
      };
      EventBus.on("grid:add-slot", onAddSlotHandler);
    });

    // Cleanup on component unmount
    onBeforeUnmount(() => {
      // Remove event listeners
      window.removeEventListener("resize", updateCellHeight);
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleWindowBlur);

      // Cancel debounced handlers
      if (debouncedSyncSlots) {
        debouncedSyncSlots.cancel();
      }

      // Remove EventBus listeners
      if (onAddSlotHandler) {
        EventBus.off && EventBus.off("grid:add-slot", onAddSlotHandler);
      }

      // Destroy GridStack instance
      if (state.grid && gridContainer.value && gridContainer.value.parentNode) {
        state.grid.destroy(false);
      }
    });

    // Return reactive references and methods for template usage
    return {
      gridContainer,
      state,
      removeSlot,
      isCard,
      updateCellHeight,
      gridItems,
      layout,
      layoutHistory,
      undoLastChange,
    };
  },
};
</script>
<style scoped lang="scss">
.grid-layout__main-container {
  width: 100%;
  position: relative;
}

.selection-dnd {
  cursor: move;
  border: 2px solid #ea5455;
}
</style>
